import { supabase } from '@/config/supabase'
import { useAuthStore } from '@/stores/auth.store'
import { usePortalStore } from '@/stores/portal.store'
import type { Fatura } from '@/types/database'
import type { ListFilters, PaginatedResult } from './associado.service'

export type FaturaExpanded = Fatura & {
    associado_nome?: string
    contrato_ref?: string
}

export const faturaService = {
    async list(filters: ListFilters & { associado_id?: string; contrato_id?: string; tipo?: string; cpf?: string } = {}): Promise<PaginatedResult<FaturaExpanded>> {
        const { status, associado_id, contrato_id, cpf, page = 1, pageSize = 10 } = filters

        const authUser = useAuthStore.getState().user
        const portalCpf = cpf || usePortalStore.getState().cpf

        // If we have a CPF (Portal Mode or specific CPF search), use the unified RPC
        if (portalCpf) {
            // @ts-ignore
            const { data, error } = await supabase.schema('veicular_system').rpc('get_portal_faturas_por_cpf', { p_cpf: portalCpf })
            if (error) throw error
            const items = ((data as any[]) || []) as FaturaExpanded[]
            return { data: items, total: items.length, page: 1, pageSize: 50, totalPages: 1 }
        }

        let query = supabase
            .schema('veicular_system')
            .from('veic_faturas')
            .select('*, associado:veic_associados(nome), contrato:veic_contratos(external_reference, metadata, veiculo:veic_veiculos(placa))', { count: 'exact' })
            .is('deleted_at', null)
            .order('data_vencimento', { ascending: false })

        if (status && status !== 'todos') query = query.eq('status', status)
        if (associado_id) query = query.eq('associado_id', associado_id)
        if (contrato_id) query = query.eq('contrato_id', contrato_id)

        const from = (page - 1) * pageSize
        query = query.range(from, from + pageSize - 1)

        const { data, count, error } = await query
        if (error) throw error
        return {
            data: (data || []) as FaturaExpanded[],
            total: count || 0, page, pageSize,
            totalPages: Math.ceil((count || 0) / pageSize),
        }
    },

    async getById(id: string): Promise<FaturaExpanded | null> {
        const { data, error } = await supabase.schema('veicular_system').from('veic_faturas').select('*, associado:veic_associados(nome)').eq('id', id).single()
        if (error) throw error
        return data as FaturaExpanded
    },

    getStats(faturas: FaturaExpanded[]) {
        const pagas = faturas.filter(f => f.status === 'paga')
        const pendentes = faturas.filter(f => f.status === 'pendente')
        const atrasadas = faturas.filter(f => f.status === 'atrasada')
        const sum = (arr: FaturaExpanded[]) => arr.reduce((s, f) => s + Number(f.valor), 0)
        return {
            totalRecebido: sum(pagas),
            totalPendente: sum(pendentes),
            totalAtrasado: sum(atrasadas),
            qtdPagas: pagas.length,
            qtdPendentes: pendentes.length,
            qtdAtrasadas: atrasadas.length,
        }
    },

    async marcarPaga(id: string): Promise<void> {
        // @ts-ignore
        const { error } = await supabase.schema('veicular_system').from('veic_faturas').update({
            status: 'paga',
            data_pagamento: new Date().toISOString().split('T')[0],
        } as any).eq('id', id)
        if (error) throw error
    },

    async gerarProRata(faturaId: string): Promise<void> {
        // Implementação real via Supabase/Edge Function seria chamada aqui
        console.log('Gerar pro-rata para:', faturaId)
    },

    async consolidarServicos(associadoId: string): Promise<void> {
        // Agrupa todos os serviços de contratos ativos que não têm fatura pendente
        console.log(`Consolidando faturamento para associado ${associadoId}`)
    },

    async syncAsaas(params: { associado_id?: string; cpf?: string }): Promise<void> {
        const { error } = await supabase.functions.invoke('asaas-sync-charges', {
            body: params
        })
        if (error) throw error
    }
}
